import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { FileText, PaperPlaneTilt } from '@phosphor-icons/react';
import { formatCurrency, formatDate, formatTime, getStatusColor } from '../../lib/utils';

export const DODDClaimsTable = ({ claims, loading, onSubmitClaim, onCreateNew }) => {
  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (claims.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="text-center py-12">
            <FileText size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No DODD claims found</p>
            <Button variant="link" className="text-indigo-600 mt-2" onClick={onCreateNew}>
              Create your first DODD claim
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Claim #</TableHead>
              <TableHead>Patient</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Units</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>EVV</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {claims.map((claim) => (
              <TableRow key={claim.id}>
                <TableCell><span className="font-mono text-xs">{claim.claim_number}</span></TableCell>
                <TableCell><p className="font-medium">{claim.patient_name}</p></TableCell>
                <TableCell><p className="text-sm">{claim.employee_name}</p></TableCell>
                <TableCell>
                  <div>
                    <p className="text-sm">{formatDate(claim.service_date)}</p>
                    <p className="text-xs text-slate-500">
                      {formatTime(claim.time_in)} - {formatTime(claim.time_out)}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="font-mono font-semibold text-indigo-700">{claim.service_code}</span>
                  <p className="text-xs text-slate-500">{claim.waiver_type}</p>
                </TableCell>
                <TableCell><span className="font-mono">{claim.units}</span></TableCell>
                <TableCell>
                  <span className="font-mono font-medium">{formatCurrency(claim.estimated_reimbursement)}</span>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      claim.evv_status === 'Validated'
                        ? 'bg-emerald-50 text-emerald-700'
                        : claim.evv_status === 'Pending'
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-slate-50 text-slate-600'
                    }
                  >
                    {claim.evv_status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={getStatusColor(claim.status)}>
                    {claim.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {claim.status === 'Draft' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSubmitClaim(claim.id)}
                      data-testid={`submit-dodd-${claim.id}`}
                    >
                      <PaperPlaneTilt size={14} className="mr-1" />
                      Submit
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
